<script setup lang="ts">
import type { Toast } from "./toast.interface"
import {
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastRoot,
  ToastTitle,
} from "reka-ui"
import { Icon } from "@/components/ui"

defineProps<{ toast: Toast }>()

const emit = defineEmits<{ (e: "close"): void }>()
</script>

<template>
  <ToastRoot
    class="gpx-toast-root flex items-start gap-4 pointer-events-auto p-4 pr-5 w-full box-border rounded-lg shadow-[rgba(0,0,0,0.35)_0px_10px_38px_-10px,rgba(0,0,0,0.2)_0px_10px_20px_-15px]"
    :class="[
      {
        'bg-primary text-primary-card-text': toast.variant === 'default',
        'bg-theme-success text-white': toast.variant === 'success',
        'bg-theme-danger text-white': toast.variant === 'danger',
      },
    ]"
    data-test="toast-root"
    data-ignore="toast-root"
    @swipe-end="emit('close')"
    @close="emit('close')"
    @swipe-cancel="emit('close')"
  >
    <div class="flex flex-col w-full">
      <div
        v-if="toast.title || toast.icon"
        class="flex flex-row items-center gap-2 mb-2 whitespace-nowrap"
      >
        <Icon v-if="toast.icon" :icon="toast.icon" />
        <ToastTitle
          v-if="toast.title"
          class="text-base mr-4 m-0 font-medium md:text-lg"
          data-test="toast-title"
        >
          {{ toast.title }}
        </ToastTitle>
      </div>
      <ToastDescription
        v-if="toast.description"
        class="font-montserrat font-normal text-sm m-0 max-w-62.5 leading-[1.3]"
      >
        {{ toast.description }}
      </ToastDescription>
    </div>
    <div class="flex flex-col justify-between self-stretch items-end">
      <div>
        <ToastClose
          class="cursor-pointer bg-transparent border-none text-white p-0 ml-auto grow-0"
          @click="emit('close')"
        >
          <Icon icon="Close" size="sm" />
        </ToastClose>
      </div>
      <div>
        <ToastAction
          v-if="toast.action"
          class="text-xs whitespace-nowrap cursor-pointer"
          :alt-text="toast.action.label"
          as-child
          @click="toast.action.onClick"
        >
          <button v-if="toast.action.onClick">
            {{ toast.action.label }}
          </button>
          <a
            v-else-if="toast.action.href"
            :href="toast.action.href"
            :target="toast.action.target"
            :rel="toast.action.rel"
          >
            {{ toast.action.label }}
          </a>
        </ToastAction>
      </div>
    </div>
  </ToastRoot>
</template>

<style>
.gpx-toast-root[data-state="open"] {
  animation: slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1);
}

.gpx-toast-root[data-state="closed"] {
  animation: hide 100ms ease-in;
}

.gpx-toast-root[data-swipe="move"] {
  transform: translateX(var(--reka-toast-swipe-move-x));
}

.gpx-toast-root[data-swipe="cancel"] {
  transform: translateX(0);
  transition: transform 200ms ease-out;
}

.gpx-toast-root[data-swipe="end"] {
  animation: swipeOut 100ms ease-out;
}

@keyframes hide {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes slideIn {
  from {
    transform: translateX(calc(100% + 25px));
  }
  to {
    transform: translateX(0);
  }
}

@keyframes swipeOut {
  from {
    transform: translateX(var(--reka-toast-swipe-end-x));
  }
  to {
    transform: translateX(calc(100% + 25px));
  }
}
</style>
